import { nanoid } from "nanoid";
import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import "firebase/compat/firestore";
import { getRecoil } from "recoil-nexus";

import organizationIdState from "../../../../../atoms/organizationIdSelector";
import ProtocolData from "../../../../../data/ProtocolData";
import ClientData from "../../../../../data/ClientData";
import GlobalData from "../../../../../data/GlobalData";
import {
  evaluatePlanAssignmentEligibility,
  getPlanTypeLabel,
} from "./assignmentRules";
import { buildVersionDiff } from "./versionDiff";
import { normalizePlanLifecycleStatus } from "./planLifecycleStatus";
import { normalizeFirestoreDate } from "../../../../../lib/firestoreDate";

export { evaluatePlanAssignmentEligibility, getPlanTypeLabel } from "./assignmentRules";
export { normalizePlanLifecycleStatus } from "./planLifecycleStatus";

const DEFAULT_PLAN_STATUS = "active";
const REVISION_DEFAULT_PAGE_SIZE = 25;
const DEFAULT_VERSION_NUMBER = 1;
const LEGACY_VERSION_NUMBER = 0;

function toVersionNumber(value, fallback = DEFAULT_VERSION_NUMBER) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : fallback;
}

function normalizeVersionMeta(planData = {}, fallbackPlanId = null) {
  const currentMeta = planData?.v2Meta || {};
  const hasExplicitVersionNumber = Object.prototype.hasOwnProperty.call(
    currentMeta,
    "versionNumber",
  );
  const isLegacyPlan =
    !hasExplicitVersionNumber && !currentMeta.versionRootId && !currentMeta.createdByEditor;
  const rootId = currentMeta.versionRootId || fallbackPlanId || planData?.id || null;
  const createdOnRaw = currentMeta.versionCreatedOn || planData?.createdOn || new Date();
  const versionCreatedOn = normalizeFirestoreDate(createdOnRaw) ?? new Date();
  return {
    ...currentMeta,
    status: normalizePlanLifecycleStatus(
      currentMeta.status || planData?.status || DEFAULT_PLAN_STATUS,
    ),
    versionRootId: rootId,
    versionNumber: isLegacyPlan
      ? LEGACY_VERSION_NUMBER
      : toVersionNumber(currentMeta.versionNumber),
    versionCreatedOn,
  };
}

function getOrganizationId() {
  return getRecoil(organizationIdState) || localStorage.getItem("organizationId");
}

function getCurrentUserId() {
  return localStorage.getItem("uid");
}

function mapPlanData(docOrData) {
  const isFirestoreSnapshot =
    docOrData &&
    typeof docOrData.data === "function" &&
    typeof docOrData.id === "string";
  const data = isFirestoreSnapshot ? docOrData.data() : docOrData;
  const fallbackId = isFirestoreSnapshot ? docOrData.id : undefined;
  const planId = data?.id ?? fallbackId;
  const normalizedMeta = normalizeVersionMeta(data, planId);
  return {
    ...data,
    id: planId,
    v2Meta: normalizedMeta,
    status: normalizedMeta.status || DEFAULT_PLAN_STATUS,
    planType: data?.clientUID ? "client" : "template",
  };
}

function getProtocolDocRef(organizationId, planId) {
  return firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("protocols")
    .doc(planId);
}

function getRevisionCollectionRef(organizationId, planId) {
  return getProtocolDocRef(organizationId, planId).collection("revisions");
}

function getTrainingPlanAssignmentCollectionRef(organizationId) {
  return firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("trainingPlanAssignments");
}

function getProtocolExerciseDocRef(organizationId, exerciseId) {
  return firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("protocolExercises")
    .doc(exerciseId);
}

function sanitizePlanForSnapshot(planData = {}) {
  const plan = { ...planData };
  const revisions = plan?.v2Meta?.revisions;
  if (revisions) {
    plan.v2Meta = { ...plan.v2Meta };
    delete plan.v2Meta.revisions;
  }
  return plan;
}

async function commitWritesInChunks(writeJobs = []) {
  const chunkSize = 425;
  for (let i = 0; i < writeJobs.length; i += chunkSize) {
    const batch = firebase.firestore().batch();
    const chunk = writeJobs.slice(i, i + chunkSize);
    chunk.forEach((writeJob) => writeJob(batch));
    await batch.commit();
  }
}

function normalizeCoachNote(note) {
  if (typeof note !== "string") {
    return null;
  }
  const trimmed = note.trim();
  return trimmed ? trimmed : null;
}

/** Label for a forked copy when assigning with "create variant" so coaches keep source plan context. */
function buildVariantAssignmentPlanName(sourcePlanName, date = new Date()) {
  const dateStr = date.toISOString().slice(0, 10);
  const base = typeof sourcePlanName === "string" ? sourcePlanName.trim() : "";
  return `${base || "Plan"} (var: ${dateStr})`;
}

async function recordTrainingPlanAssignmentEvent({
  organizationId,
  sourcePlanId = null,
  sourcePlanName = null,
  assignedPlanId = null,
  assignedPlanName = null,
  clientId = null,
  mode = "active",
  createVariant = false,
  coachNote = null,
  assignedOn = new Date(),
  clientNameSnapshot = null,
} = {}) {
  if (!organizationId || !assignedPlanId || !clientId) {
    return null;
  }
  const eventId = nanoid();
  const docRef = getTrainingPlanAssignmentCollectionRef(organizationId).doc(eventId);
  const value = {
    id: eventId,
    planId: assignedPlanId,
    sourcePlanId: sourcePlanId || assignedPlanId,
    clientId,
    mode,
    createVariant: !!createVariant,
    coachNote: normalizeCoachNote(coachNote),
    actor: getCurrentUserId() || null,
    assignedOn: assignedOn || new Date(),
    planNameSnapshot: assignedPlanName || sourcePlanName || null,
    sourcePlanNameSnapshot: sourcePlanName || null,
    clientNameSnapshot: clientNameSnapshot || null,
  };
  await docRef.set(value, { merge: true });
  return value;
}

export function isTrainingPlansV2Enabled() {
  if (typeof localStorage !== "undefined") {
    if (localStorage.getItem("trainingPlansV2Disabled") === "true") {
      return false;
    }
    if (localStorage.getItem("trainingPlansV2ForceEnable") === "true") {
      return true;
    }
  }
  const orgId = getOrganizationId();
  if (!orgId) return false;
  return true;
}

export async function setTrainingPlansV2Enabled(enabled) {
  localStorage.setItem("trainingPlansV2Disabled", enabled ? "false" : "true");
}

export async function trackTrainingPlansV2Event(eventName, payload = {}) {
  const organizationId = getOrganizationId();
  const coachId = getCurrentUserId();
  if (!organizationId || !coachId) {
    return;
  }

  try {
    firebase.analytics().logEvent("training_plans_v2_event", {
      eventName,
      experimentKey: "training_plans_v2_parallel_adoption",
      entrypoint: payload.entrypoint || "training_plans_v2",
      coachId,
      organizationId,
      ...payload,
    });
  } catch (e) {
    // analytics failures are non-blocking
  }

  const dayKey = new Date().toISOString().slice(0, 10);
  const metricsRef = firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("trainingPlansV2MetricsDaily")
    .doc(dayKey);

  await metricsRef.set(
    {
      date: dayKey,
      updatedOn: new Date(),
      [`events.${eventName}`]: firebase.firestore.FieldValue.increment(1),
      [`entrypoints.${payload.entrypoint || "training_plans_v2"}`]:
        firebase.firestore.FieldValue.increment(1),
    },
    { merge: true },
  );
}

export async function listPlans({
  search = "",
  type = "template",
  status = "all",
  sort = "updated_desc",
  clientUID = "",
  protocolLabelIds = [],
  pageSize = 25,
  startAfterDoc = null,
} = {}) {
  const organizationId = getOrganizationId();
  if (!organizationId) {
    return { plans: [], lastDoc: null, hasMore: false };
  }

  const normalizedSearch = search.trim();
  const labelFilterIds = (
    Array.isArray(protocolLabelIds) ? protocolLabelIds.filter(Boolean).slice(0, 10) : []
  ).map(String);
  const hasLabelFilter = labelFilterIds.length > 0;
  const useNamePrefixSearch = Boolean(normalizedSearch) && !hasLabelFilter;
  let query = firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("protocols");

  if (type === "client") {
    if (!clientUID) {
      return { plans: [], lastDoc: null, hasMore: false };
    }
    query = query.where("clientUID", "==", clientUID);
  } else {
    query = query.where("clientUID", "==", null);
  }

  if (hasLabelFilter) {
    query = query.where("protocolLabels", "array-contains-any", labelFilterIds);
  }

  if (useNamePrefixSearch) {
    query = query
      .orderBy("name", "asc")
      .startAt(normalizedSearch)
      .endAt(`${normalizedSearch}\uf8ff`);
  } else if (sort === "name_asc") {
    query = query.orderBy("name", "asc");
  } else {
    query = query.orderBy("updatedOn", "desc");
  }

  if (startAfterDoc) {
    query = query.startAfter(startAfterDoc);
  }

  const snapshot = await query.limit(pageSize + 1).get();
  const hasMore = snapshot.docs.length > pageSize;
  const pageDocs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;

  let plans = pageDocs.map((doc) => mapPlanData(doc));
  if (hasLabelFilter && normalizedSearch) {
    const needle = normalizedSearch.toLowerCase();
    plans = plans.filter((plan) => String(plan?.name || "").toLowerCase().includes(needle));
  }
  if (status === "in_progress") {
    plans = plans.filter(
      (plan) => plan.status === "draft" || plan.status === "active",
    );
  } else if (status !== "all") {
    plans = plans.filter((plan) => plan.status === status);
  }

  if (!useNamePrefixSearch) {
    const getVersion = (plan) => {
      const parsed = Number(plan?.v2Meta?.versionNumber);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    const getUpdatedAt = (plan) =>
      normalizeFirestoreDate(plan.updatedOn) ?? new Date(0);
    const byNameAsc = (a, b) => (a.name || "").localeCompare(b.name || "");
    const byUpdatedDesc = (a, b) => getUpdatedAt(b) - getUpdatedAt(a);
    const byIdAsc = (a, b) => (a.id || "").localeCompare(b.id || "");

    const comparePlans = (a, b) => {
      if (sort === "version_desc") {
        const versionDiff = getVersion(b) - getVersion(a);
        if (versionDiff !== 0) return versionDiff;
        const nameDiff = byNameAsc(a, b);
        if (nameDiff !== 0) return nameDiff;
        const updatedDiff = byUpdatedDesc(a, b);
        if (updatedDiff !== 0) return updatedDiff;
        return byIdAsc(a, b);
      }
      if (sort === "version_asc") {
        const versionDiff = getVersion(a) - getVersion(b);
        if (versionDiff !== 0) return versionDiff;
        const nameDiff = byNameAsc(a, b);
        if (nameDiff !== 0) return nameDiff;
        const updatedDiff = byUpdatedDesc(a, b);
        if (updatedDiff !== 0) return updatedDiff;
        return byIdAsc(a, b);
      }
      if (sort === "name_asc") {
        const nameDiff = byNameAsc(a, b);
        if (nameDiff !== 0) return nameDiff;
        const updatedDiff = byUpdatedDesc(a, b);
        if (updatedDiff !== 0) return updatedDiff;
        return byIdAsc(a, b);
      }
      const updatedDiff = byUpdatedDesc(a, b);
      if (updatedDiff !== 0) return updatedDiff;
      const nameDiff = byNameAsc(a, b);
      if (nameDiff !== 0) return nameDiff;
      return byIdAsc(a, b);
    };

    plans.sort((a, b) => {
      if (status === "in_progress") {
        const draftRank = (p) => (p.status === "draft" ? 0 : 1);
        const dr = draftRank(a) - draftRank(b);
        if (dr !== 0) return dr;
      }
      return comparePlans(a, b);
    });
  }

  return {
    plans,
    lastDoc: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
    hasMore,
  };
}

export async function getPlansByIds(planIds = []) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planIds.length) {
    return [];
  }

  const uniqueIds = Array.from(new Set(planIds.filter(Boolean)));
  const docs = await Promise.all(
    uniqueIds.map((id) =>
      firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocols")
        .doc(id)
        .get(),
    ),
  );

  const mappedById = {};
  docs.forEach((doc) => {
    if (doc.exists) {
      const mapped = mapPlanData(doc);
      mappedById[mapped.id] = mapped;
    }
  });

  return uniqueIds.map((id) => mappedById[id]).filter(Boolean);
}

export async function getPlanById(planId) {
  const protocol = await ProtocolData.getProtocol(planId);
  if (!protocol) {
    return null;
  }
  return mapPlanData(protocol.data());
}

export async function createPlan({
  name,
  description = "",
  planType = "template",
  clientUID = null,
  v2Meta = {},
}) {
  if (planType === "client" && !clientUID) {
    throw new Error("Client plans require a selected client.");
  }

  const id = nanoid();
  const baseMeta = normalizeVersionMeta(
    {
      id,
      createdOn: new Date(),
      v2Meta: {
        versionNumber: DEFAULT_VERSION_NUMBER,
        ...(v2Meta || {}),
      },
      status: "draft",
    },
    id,
  );
  const payload = {
    id,
    name: name || `New Training Plan - ${new Date().toISOString().slice(0, 10)}`,
    description,
    privacy: "private",
    type: "general",
    difficulty: "beginner",
    protocolLabels: [],
    clientUID: planType === "client" ? clientUID : null,
    createdOn: new Date(),
    updatedOn: new Date(),
    v2Meta: {
      ...baseMeta,
      status: "draft",
      createdByEditor: "v2",
    },
  };

  await ProtocolData.addProtocol(payload);
  await appendRevision(id, {
    action: "create",
    summary: "Plan created in V2",
  });
  return payload;
}

export async function savePlanMetadata(planId, values, options = {}) {
  const { allowPromotionFromArchivedDuplicate = false } = options;
  const current = await getPlanById(planId);
  const currentStatus = normalizeVersionMeta(current || { id: planId }, planId).status || "active";
  const requestedStatus = normalizePlanLifecycleStatus(
    values?.v2Meta && Object.prototype.hasOwnProperty.call(values.v2Meta, "status")
      ? values.v2Meta.status
      : currentStatus,
  );
  const nextPlanType = values?.planType ?? current?.planType ?? "template";
  const hasClientUIDOverride = Object.prototype.hasOwnProperty.call(values || {}, "clientUID");
  const nextClientUID = hasClientUIDOverride ? values?.clientUID : current?.clientUID;
  if (nextPlanType === "client" && !nextClientUID) {
    throw new Error("Client plans require a selected client.");
  }
  // Duplicated protocol docs copy archived v2Meta from the source; createVersionDraft then promotes
  // the new id to draft/active. Without this escape hatch, save throws after duplicate succeeds and
  // leaves orphan archived copies.
  if (
    !allowPromotionFromArchivedDuplicate &&
    currentStatus === "archived" &&
    ["draft", "active"].includes(requestedStatus)
  ) {
    throw new Error(
      "Archived versions are read-only. Restore or create a draft version in Manage before publishing.",
    );
  }
  // Once a currently active version is assigned, it cannot be demoted in place.
  if (currentStatus === "active" && requestedStatus !== "active") {
    const coverage = await getPlanAssignmentCoverage({ planIds: [planId] });
    const assignment = coverage?.[planId] || { primaryCount: 0, alternativeCount: 0 };
    if ((assignment.primaryCount || 0) > 0 || (assignment.alternativeCount || 0) > 0) {
      throw new Error(
        "Assigned plans must remain active. Create a new version draft for edits, then publish.",
      );
    }
  }
  const nextValues = {
    ...current,
    ...values,
    id: planId,
    planType: nextPlanType,
    clientUID: nextPlanType === "client" ? nextClientUID : null,
    updatedOn: new Date(),
    v2Meta: {
      ...normalizeVersionMeta(current || { id: planId }, planId),
      ...(values?.v2Meta || {}),
      lastEditedByEditor: "v2",
    },
  };
  nextValues.v2Meta.status = normalizePlanLifecycleStatus(nextValues.v2Meta.status);
  await ProtocolData.addProtocol(nextValues);
  await appendRevision(planId, {
    action: "update",
    summary: "Plan metadata updated in V2",
  });
  return nextValues;
}

export async function setPlanStatus(plan, status, { summary = "" } = {}) {
  const currentStatus = normalizePlanLifecycleStatus(plan?.v2Meta?.status || plan?.status || "active");
  const nextStatus = normalizePlanLifecycleStatus(status);
  if (currentStatus === "archived" && ["draft", "active"].includes(nextStatus)) {
    throw new Error(
      "Archived versions are read-only. Restore or create a draft version in Manage before publishing.",
    );
  }
  if (currentStatus === "draft" && nextStatus === "active") {
    return publishVersionDraft({
      draftPlanId: plan.id,
      summary: summary || "Published via status change",
    });
  }
  return savePlanMetadata(plan.id, {
    v2Meta: {
      ...(plan.v2Meta || {}),
      status: nextStatus,
    },
  });
}

export async function getSplits(planId) {
  const splits = await ProtocolData.getProtocolSplits(planId);
  return splits.map((s) => s.data());
}

export async function upsertSplit(split, isAdd = false) {
  await ProtocolData.addSplitFlat(split, isAdd);
}

export async function deleteSplit(planId, splitId) {
  await ProtocolData.deleteSplit(planId, splitId);
}

export async function duplicateSplit(planId, split, count, options = {}) {
  const { showCopyAppend = true } = options;
  await ProtocolData.duplicateSplit({
    protocolId: planId,
    split,
    count,
    showCopyAppend,
  });
}

/**
 * Copy a workout (split) + its exercises from any source plan into targetPlanId.
 * Appends at `index` (typically current split count). Keeps the source workout name.
 */
export async function copySplitIntoPlan({ targetPlanId, sourceSplit, index }) {
  if (!targetPlanId || !sourceSplit?.id) {
    throw new Error("Target plan and source workout are required.");
  }
  await duplicateSplit(targetPlanId, sourceSplit, index, { showCopyAppend: false });
}

export async function reorderSplits(planId, splits) {
  await ProtocolData.setSplitOrder(planId, splits);
}

export async function getSplitExercises(splitId) {
  const docs = await ProtocolData.getSplitExercises(splitId);
  return docs.map((d) => d.data());
}

export async function getExerciseCatalog() {
  try {
    const exercises = await GlobalData.getExercises();
    return (exercises || [])
      .filter((exercise) => !exercise?.isDeleted)
      .map((exercise) => ({
        id: exercise.id,
        name: exercise.name || exercise.id,
        type: exercise.type || "",
        isCustom: !!exercise.isCustom,
      }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  } catch (error) {
    // Fallback prevents editor boot failure if legacy exercise batch helpers are unavailable.
    console.error("trainingPlansV2Service.getExerciseCatalog fallback:", error);
    return [];
  }
}

export async function upsertExercise(exercise) {
  await ProtocolData.addSplitExerciseFlat(exercise);
}

export async function deleteExercise(exercise) {
  await ProtocolData.deleteSplitExercise(exercise);
}

export async function reorderExercises(planId, exercises) {
  await ProtocolData.setExerciseOrder(planId, exercises);
}

export async function bulkUpsertExercises(exercises = []) {
  const organizationId = getOrganizationId();
  if (!organizationId || !Array.isArray(exercises) || exercises.length === 0) return;
  const writes = exercises
    .filter((exercise) => exercise?.id)
    .map((exercise) => (batch) => {
      const docRef = getProtocolExerciseDocRef(organizationId, exercise.id);
      batch.set(docRef, exercise, { merge: true });
    });
  await commitWritesInChunks(writes);
}

export async function bulkDeleteExercises(exercises = []) {
  const organizationId = getOrganizationId();
  if (!organizationId || !Array.isArray(exercises) || exercises.length === 0) return;
  const writes = exercises
    .filter((exercise) => exercise?.id)
    .map((exercise) => (batch) => {
      const docRef = getProtocolExerciseDocRef(organizationId, exercise.id);
      batch.delete(docRef);
    });
  await commitWritesInChunks(writes);
}

export async function duplicatePlan({ planId, name = null, clientUID = null }) {
  return ProtocolData.duplicateProtocol({ protocolId: planId, name, clientUID });
}

/**
 * Duplicate protocol content with no `clientUID` so the copy is org-wide (reusable) and can be
 * assigned to any client. Passing `clientUID` into {@link duplicatePlan} keeps a client-scoped copy.
 */
export async function duplicatePlanAsReusable({ planId, name }) {
  if (!planId) {
    throw new Error("Plan id is required.");
  }
  return duplicatePlan({ planId, name, clientUID: null });
}

export async function listVersionFamily({ planId, limit = 100 } = {}) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId) {
    return { rootId: null, versions: [] };
  }

  const currentPlan = await getPlanById(planId);
  if (!currentPlan) {
    return { rootId: null, versions: [] };
  }
  const rootId = currentPlan?.v2Meta?.versionRootId || currentPlan.id;
  const protocolsRef = firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("protocols");

  const [snapshot, rootDoc] = await Promise.all([
    protocolsRef.where("v2Meta.versionRootId", "==", rootId).limit(limit).get(),
    protocolsRef.doc(rootId).get(),
  ]);

  const versionsById = new Map(
    snapshot.docs.map((doc) => [doc.id, mapPlanData({ id: doc.id, ...doc.data() })]),
  );
  if (rootDoc.exists && !versionsById.has(rootDoc.id)) {
    versionsById.set(rootDoc.id, mapPlanData({ id: rootDoc.id, ...rootDoc.data() }));
  }

  const versions = Array.from(versionsById.values())
    .sort((a, b) => {
      const aNumber = toVersionNumber(a?.v2Meta?.versionNumber);
      const bNumber = toVersionNumber(b?.v2Meta?.versionNumber);
      if (aNumber !== bNumber) return aNumber - bNumber;
      const aCreated = normalizeFirestoreDate(a?.v2Meta?.versionCreatedOn) ?? new Date(0);
      const bCreated = normalizeFirestoreDate(b?.v2Meta?.versionCreatedOn) ?? new Date(0);
      return aCreated - bCreated;
    });

  return { rootId, versions };
}

function getNextVersionNumber(versions = []) {
  return (
    versions.reduce((max, version) => {
      return Math.max(max, toVersionNumber(version?.v2Meta?.versionNumber));
    }, 0) + 1
  );
}

export async function createVersionDraft({
  sourcePlanId,
  name = null,
  summary = "",
  restoreFromArchivedVersion = false,
} = {}) {
  if (!sourcePlanId) {
    throw new Error("Source plan is required to create a version draft.");
  }

  const sourcePlan = await getPlanById(sourcePlanId);
  if (!sourcePlan) {
    throw new Error("Source plan not found.");
  }

  const family = await listVersionFamily({ planId: sourcePlanId, limit: 200 });
  const rootId = family.rootId || sourcePlan.id;
  const nextVersionNumber = getNextVersionNumber(family.versions || []);
  const hasActiveInFamily = (family.versions || []).some(
    (version) =>
      normalizePlanLifecycleStatus(
        version?.v2Meta?.status || version?.status || DEFAULT_PLAN_STATUS,
      ) === "active",
  );
  const nextStatus =
    restoreFromArchivedVersion && !hasActiveInFamily ? "active" : "draft";
  const duplicated = await duplicatePlan({
    planId: sourcePlanId,
    name: name || sourcePlan.name,
    clientUID: sourcePlan.clientUID || null,
  });

  await savePlanMetadata(
    duplicated.id,
    {
      name: name || sourcePlan.name,
      description: sourcePlan.description || "",
      clientUID: sourcePlan.clientUID || null,
      v2Meta: {
        ...(sourcePlan.v2Meta || {}),
        status: nextStatus,
        versionRootId: rootId,
        versionNumber: nextVersionNumber,
        versionCreatedOn: new Date(),
        parentPlanId: sourcePlanId,
        publishedOn: nextStatus === "active" ? new Date() : null,
      },
    },
    { allowPromotionFromArchivedDuplicate: true },
  );

  const sourceVersionLabel = toVersionNumber(sourcePlan?.v2Meta?.versionNumber);
  await appendRevision(duplicated.id, {
    action: nextStatus === "active" ? "version_restore_active" : "version_draft_create",
    summary:
      summary ||
      (nextStatus === "active"
        ? `Restored V${sourceVersionLabel} as V${nextVersionNumber} (active)`
        : `Created V${nextVersionNumber} draft from V${sourceVersionLabel}`),
  });

  return getPlanById(duplicated.id);
}

export async function restoreVersionAsDraft({
  sourceVersionPlanId,
  summary = "",
} = {}) {
  if (!sourceVersionPlanId) {
    throw new Error("Version plan is required for restore.");
  }
  return createVersionDraft({
    sourcePlanId: sourceVersionPlanId,
    summary: summary || "Restored historical version as new draft",
    restoreFromArchivedVersion: true,
  });
}

export async function deletePlan(planId) {
  return ProtocolData.deleteProtocol(planId);
}

/** Destructive delete for archived versioned plans (server enforces archived + unassigned). */
export async function deleteArchivedPlanPermanently(planId) {
  if (!planId) {
    throw new Error("Plan id is required.");
  }
  return ProtocolData.deleteProtocol(planId, { archivedOnly: true });
}

export async function getAssignableClients() {
  const uid = getCurrentUserId();
  if (!uid) {
    return [];
  }
  return ClientData.getClients(uid);
}

export async function assignPlanToClient({
  clientId,
  planId,
  mode = "active",
  createVariant = false,
  variantAsDraft = false,
  coachNote = null,
  sourcePlanId = null,
  sourcePlanName = null,
  clientNameSnapshot = null,
}) {
  if (variantAsDraft) {
    if (mode !== "active") {
      throw new Error("Assign as variant draft is only supported for primary assignment.");
    }
    if (!createVariant) {
      throw new Error("Assign as variant draft requires creating a variant copy.");
    }
  }

  const organizationId = getOrganizationId();
  const sourcePlan = await getPlanById(planId);
  const sourceStatus = sourcePlan?.v2Meta?.status || sourcePlan?.status || "active";
  if (sourceStatus !== "active") {
    throw new Error("Only published active plans can be assigned. Use Publish and Assign to continue.");
  }
  const eligibility = evaluatePlanAssignmentEligibility({
    sourcePlan,
    targetClientId: clientId,
    mode,
    createVariant: createVariant || variantAsDraft,
  });
  if (!eligibility.allowed) {
    throw new Error(eligibility.reason || "This plan cannot be assigned with the selected settings.");
  }

  let finalPlanId = planId;
  let finalPlanName = sourcePlan?.name || null;

  if (createVariant) {
    const variant = await duplicatePlan({
      planId,
      name: buildVariantAssignmentPlanName(sourcePlan?.name),
      clientUID: clientId,
    });
    finalPlanId = variant.id;
    finalPlanName = variant?.name || finalPlanName;

    if (variantAsDraft) {
      const forkedAt = new Date();
      const freshVariant = await getPlanById(finalPlanId);
      await savePlanMetadata(finalPlanId, {
        clientUID: clientId,
        planType: "client",
        v2Meta: {
          ...(freshVariant?.v2Meta || {}),
          status: "draft",
          versionRootId: finalPlanId,
          versionNumber: 1,
          versionCreatedOn: forkedAt,
          publishedOn: null,
          parentPlanId: planId,
        },
      });
    } else {
      const freshVariant = await getPlanById(finalPlanId);
      await savePlanMetadata(finalPlanId, {
        clientUID: clientId,
        planType: "client",
        v2Meta: {
          ...(freshVariant?.v2Meta || {}),
          parentPlanId: planId,
        },
      });
      await publishVersionDraft({
        draftPlanId: finalPlanId,
        summary: "Variant published for assignment",
      });
    }
  }

  if (!variantAsDraft) {
    if (mode === "alternative") {
      await ClientData.addAlternativeProtocol(clientId, finalPlanId);
    } else {
      await ClientData.updateClient(clientId, {
        protocolId: finalPlanId,
        protocolChanged: new Date(),
      });
    }
  }

  await appendRevision(finalPlanId, {
    action: variantAsDraft ? "variant_draft_create" : "assign",
    summary: variantAsDraft
      ? `Variant draft for client ${clientId} (primary plan unchanged)`
      : `Plan assigned in mode: ${mode}`,
    clientId,
  });

  const assignmentMode = variantAsDraft ? "variant_draft" : mode;
  await recordTrainingPlanAssignmentEvent({
    organizationId,
    sourcePlanId: sourcePlanId || planId,
    sourcePlanName: sourcePlanName || sourcePlan?.name || null,
    assignedPlanId: finalPlanId,
    assignedPlanName: finalPlanName,
    clientId,
    mode: assignmentMode,
    createVariant: !!createVariant || variantAsDraft,
    coachNote,
    assignedOn: new Date(),
    clientNameSnapshot,
  });

  return finalPlanId;
}

export async function publishAndAssignPlan({
  planId,
  clientIds = [],
  mode = "active",
  createVariant = false,
  variantAsDraft = false,
  coachNote = null,
  sourcePlanName = null,
  clientsById = {},
} = {}) {
  const targets = Array.from(new Set((clientIds || []).filter(Boolean)));
  if (!targets.length) {
    throw new Error("Select at least one client before assigning.");
  }

  const sourcePlan = await getPlanById(planId);
  if (!sourcePlan) {
    throw new Error("The selected plan could not be found.");
  }

  let publishResult = null;
  const sourceStatus = sourcePlan?.v2Meta?.status || sourcePlan?.status || "active";
  if (sourceStatus === "draft") {
    publishResult = await publishVersionDraft({
      draftPlanId: planId,
      summary: "Published as part of assign workflow",
    });
  } else if (sourceStatus !== "active") {
    throw new Error(
      "Archived versions cannot be assigned directly. Restore or create a draft version in Manage, then publish and assign.",
    );
  }

  const assignedPlanIds = await Promise.all(
    targets.map((clientId) =>
      assignPlanToClient({
        clientId,
        planId,
        mode,
        createVariant,
        variantAsDraft,
        coachNote,
        sourcePlanId: planId,
        sourcePlanName: sourcePlanName || sourcePlan?.name || null,
        clientNameSnapshot: clientsById?.[clientId]
          ? `${clientsById[clientId]?.firstName || ""} ${clientsById[clientId]?.lastName || ""}`.trim() ||
            clientsById[clientId]?.uid ||
            clientsById[clientId]?.id ||
            null
          : null,
      }),
    ),
  );

  return {
    publishedDuringAssign: !!publishResult,
    publishResult,
    assignedClientCount: targets.length,
    assignedPlanIds,
  };
}

export async function publishVersionDraft({
  draftPlanId,
  summary = "",
} = {}) {
  if (!draftPlanId) {
    throw new Error("Draft plan ID is required.");
  }

  const draftPlan = await getPlanById(draftPlanId);
  if (!draftPlan) {
    throw new Error("Draft plan not found.");
  }
  const currentStatus = normalizePlanLifecycleStatus(
    draftPlan?.v2Meta?.status || draftPlan?.status || "active",
  );
  if (currentStatus !== "draft") {
    if (currentStatus === "archived") {
      throw new Error(
        "Archived versions are read-only. Restore or create a draft version in Manage before publishing.",
      );
    }
    throw new Error("Only draft versions can be published.");
  }
  const family = await listVersionFamily({ planId: draftPlanId, limit: 200 });
  const draftVersionNumber = toVersionNumber(draftPlan?.v2Meta?.versionNumber);
  const currentActiveVersion = [...(family.versions || [])]
    .filter(
      (version) =>
        version.id !== draftPlanId &&
        version?.v2Meta?.status === "active",
    )
    .sort(
      (a, b) =>
        toVersionNumber(b?.v2Meta?.versionNumber) -
        toVersionNumber(a?.v2Meta?.versionNumber),
    )[0];

  await savePlanMetadata(draftPlanId, {
    ...draftPlan,
    v2Meta: {
      ...(draftPlan?.v2Meta || {}),
      status: "active",
      publishedOn: new Date(),
    },
  });

  let migratedClientCount = 0;
  if (currentActiveVersion?.id) {
    const assignedClients = await getPlanUsage(currentActiveVersion.id);
    migratedClientCount = assignedClients.length;
    await Promise.all(
      assignedClients.map((client) =>
        ClientData.updateClient(client.id, {
          protocolId: draftPlanId,
          protocolChanged: new Date(),
        }),
      ),
    );
    await appendRevision(currentActiveVersion.id, {
      action: "version_superseded",
      summary: `Superseded by V${draftVersionNumber}`,
    });
  }

  await appendRevision(draftPlanId, {
    action: "publish",
    summary:
      summary ||
      `Published V${draftVersionNumber}${currentActiveVersion?.id ? ` and migrated ${migratedClientCount} client(s)` : ""}`,
  });

  return {
    draftPlanId,
    previousActivePlanId: currentActiveVersion?.id || null,
    migratedClientCount,
  };
}

export async function getPlanUsage(planId) {
  const organizationId = getOrganizationId();
  if (!organizationId) {
    return [];
  }

  const snapshot = await firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("clients")
    .where("protocolId", "==", planId)
    .get();

  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export async function listPlanAssignmentHistory({
  planId,
  pageSize = 25,
  startAfterDoc = null,
} = {}) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId) {
    return { assignments: [], lastDoc: null, hasMore: false };
  }
  let query = getTrainingPlanAssignmentCollectionRef(organizationId)
    .where("planId", "==", planId)
    .orderBy("assignedOn", "desc")
    .limit(pageSize + 1);
  if (startAfterDoc) {
    query = query.startAfter(startAfterDoc);
  }
  const snapshot = await query.get();
  const hasMore = snapshot.docs.length > pageSize;
  const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs;
  return {
    assignments: docs.map((doc) => ({ id: doc.id, ...doc.data() })),
    lastDoc: docs.length ? docs[docs.length - 1] : null,
    hasMore,
  };
}

function buildPlanAssignmentCoverageFromClients(clients = [], planIds = []) {
  const shouldFilter = Array.isArray(planIds) && planIds.length > 0;
  const allowed = shouldFilter ? new Set(planIds.filter(Boolean)) : null;
  const coverage = {};

  const ensureCoverage = (id) => {
    if (!id) return null;
    if (allowed && !allowed.has(id)) return null;
    if (!coverage[id]) {
      coverage[id] = {
        primaryCount: 0,
        alternativeCount: 0,
      };
    }
    return coverage[id];
  };

  clients.forEach((client) => {
    const primaryPlanId = client?.protocolId || null;
    const primaryCoverage = ensureCoverage(primaryPlanId);
    if (primaryCoverage) {
      primaryCoverage.primaryCount += 1;
    }

    const alternatives = Array.isArray(client?.alternativeProtocols)
      ? Array.from(new Set(client.alternativeProtocols.filter(Boolean)))
      : [];
    alternatives.forEach((alternativePlanId) => {
      const alternativeCoverage = ensureCoverage(alternativePlanId);
      if (alternativeCoverage) {
        alternativeCoverage.alternativeCount += 1;
      }
    });
  });

  if (shouldFilter) {
    planIds.forEach((id) => {
      if (!id) return;
      if (!coverage[id]) {
        coverage[id] = { primaryCount: 0, alternativeCount: 0 };
      }
    });
  }

  return coverage;
}

export async function getPlanAssignmentCoverage({ planIds = [], clients = null } = {}) {
  if (Array.isArray(clients)) {
    return buildPlanAssignmentCoverageFromClients(clients, planIds);
  }

  const organizationId = getOrganizationId();
  if (!organizationId) {
    return {};
  }
  const snapshot = await firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("clients")
    .get();
  const allClients = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  return buildPlanAssignmentCoverageFromClients(allClients, planIds);
}

export async function getPublishImpactPreview({ draftPlanId } = {}) {
  if (!draftPlanId) {
    throw new Error("Draft plan ID is required.");
  }
  const draftPlan = await getPlanById(draftPlanId);
  if (!draftPlan) {
    throw new Error("Draft plan not found.");
  }

  const family = await listVersionFamily({ planId: draftPlanId, limit: 200 });
  const currentActiveVersion = [...(family.versions || [])]
    .filter((version) => version.id !== draftPlanId && version?.v2Meta?.status === "active")
    .sort(
      (a, b) =>
        toVersionNumber(b?.v2Meta?.versionNumber) - toVersionNumber(a?.v2Meta?.versionNumber),
    )[0];
  if (!currentActiveVersion?.id) {
    return {
      previousActiveVersion: null,
      migratedClientCountEstimate: 0,
    };
  }
  const assignedClients = await getPlanUsage(currentActiveVersion.id);
  return {
    previousActiveVersion: currentActiveVersion,
    migratedClientCountEstimate: assignedClients.length,
  };
}

export async function getPlanAlternatives(clientId) {
  const organizationId = getOrganizationId();
  if (!organizationId || !clientId) {
    return [];
  }
  const doc = await firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("clients")
    .doc(clientId)
    .get();
  return doc.data()?.alternativeProtocols || [];
}

export async function removeAlternativePlan(clientId, planId) {
  return ClientData.removeAlternativeProtocol(clientId, planId);
}

/** Clears the client's primary training plan assignment (protocolId), like a new client with no plan. */
export async function unassignPrimaryPlanFromClient({
  clientId,
  planId = null,
  coachNote = null,
  sourcePlanName = null,
  clientNameSnapshot = null,
} = {}) {
  if (!clientId) {
    throw new Error("Client id is required.");
  }

  const organizationId = getOrganizationId();
  if (!organizationId) {
    throw new Error("Organization is required.");
  }

  const clientRef = firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("clients")
    .doc(clientId);
  const clientSnap = await clientRef.get();
  if (!clientSnap.exists) {
    throw new Error("Client not found.");
  }

  const clientData = clientSnap.data() || {};
  const currentProtocolId = clientData.protocolId || null;
  if (!currentProtocolId) {
    throw new Error("This client does not have a primary training plan assigned.");
  }

  if (planId && currentProtocolId !== planId) {
    throw new Error(
      "This client's primary plan does not match this version. Refresh the page and try again.",
    );
  }

  const planForHistory = planId || currentProtocolId;
  const plan =
    sourcePlanName != null
      ? { name: sourcePlanName }
      : await getPlanById(planForHistory);
  const planName = plan?.name || sourcePlanName || null;

  await ClientData.updateClient(clientId, {
    protocolId: null,
    protocolChanged: new Date(),
  });

  await appendRevision(planForHistory, {
    action: "unassign",
    summary: `Primary assignment removed for client ${clientId}`,
    clientId,
  });

  await recordTrainingPlanAssignmentEvent({
    organizationId,
    sourcePlanId: planForHistory,
    sourcePlanName: planName,
    assignedPlanId: planForHistory,
    assignedPlanName: planName,
    clientId,
    mode: "unassign",
    coachNote,
    clientNameSnapshot,
  });

  return planForHistory;
}

async function buildPlanSnapshot(organizationId, planId) {
  const protocolDoc = await getProtocolDocRef(organizationId, planId).get();
  if (!protocolDoc.exists) {
    return null;
  }

  const [splitSnapshot, exerciseSnapshot] = await Promise.all([
    firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .where("protocolId", "==", planId)
      .orderBy("index", "asc")
      .get(),
    firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .where("protocolId", "==", planId)
      .get(),
  ]);

  return {
    plan: sanitizePlanForSnapshot(protocolDoc.data()),
    splits: splitSnapshot.docs.map((doc) => doc.data()),
    exercises: exerciseSnapshot.docs
      .map((doc) => doc.data())
      .sort((a, b) => {
        if (a.splitId !== b.splitId) {
          return (a.splitId || "").localeCompare(b.splitId || "");
        }
        return (a.index ?? 0) - (b.index ?? 0);
      }),
  };
}

async function writeRevisionSnapshot({
  organizationId,
  planId,
  revisionId,
  snapshot,
}) {
  const revisionDocRef = getRevisionCollectionRef(organizationId, planId).doc(revisionId);
  const snapshotRootDoc = revisionDocRef.collection("snapshot").doc("state");
  const writes = [];

  writes.push((batch) => {
    const planSnapshotRef = snapshotRootDoc.collection("plan").doc("data");
    batch.set(planSnapshotRef, {
      ...snapshot.plan,
      __meta: {
        splitCount: snapshot.splits.length,
        exerciseCount: snapshot.exercises.length,
      },
    });
  });

  snapshot.splits.forEach((split) => {
    writes.push((batch) => {
      const splitRef = snapshotRootDoc.collection("splits").doc(split.id);
      batch.set(splitRef, split);
    });
  });

  snapshot.exercises.forEach((exercise) => {
    writes.push((batch) => {
      const exerciseRef = snapshotRootDoc.collection("exercises").doc(exercise.id);
      batch.set(exerciseRef, exercise);
    });
  });

  await commitWritesInChunks(writes);
}

export async function appendRevision(planId, revision, options = {}) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId) {
    return;
  }

  const shouldCaptureSnapshot =
    options.captureSnapshot === true ||
    ["create", "publish", "revision", "restore"].includes(revision.action);

  const revisionId = nanoid();
  const revisionDocRef = getRevisionCollectionRef(organizationId, planId).doc(revisionId);

  const value = {
    id: revisionId,
    action: revision.action || "update",
    summary: revision.summary || "",
    clientId: revision.clientId || null,
    sourceRevisionId: revision.sourceRevisionId || null,
    kind: shouldCaptureSnapshot ? "version" : "event",
    actor: getCurrentUserId(),
    createdOn: new Date(),
  };

  await revisionDocRef.set(value, { merge: true });

  if (shouldCaptureSnapshot) {
    const snapshot =
      options.snapshot || (await buildPlanSnapshot(organizationId, planId));
    if (snapshot) {
      await writeRevisionSnapshot({
        organizationId,
        planId,
        revisionId,
        snapshot,
      });
    }
  }

  // Backward compatibility for old readers still checking v2Meta.revisions array.
  await getProtocolDocRef(organizationId, planId).set(
    {
      v2Meta: {
        revisions: firebase.firestore.FieldValue.arrayUnion(value),
      },
    },
    { merge: true },
  );

  return value;
}

export async function listRevisions({
  planId,
  limit = REVISION_DEFAULT_PAGE_SIZE,
  startAfterDoc = null,
} = {}) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId) {
    return { revisions: [], lastDoc: null, hasMore: false };
  }

  let query = getRevisionCollectionRef(organizationId, planId)
    .orderBy("createdOn", "desc")
    .limit(limit + 1);

  if (startAfterDoc) {
    query = query.startAfter(startAfterDoc);
  }

  const snapshot = await query.get();
  const hasMore = snapshot.docs.length > limit;
  const docs = hasMore ? snapshot.docs.slice(0, limit) : snapshot.docs;
  const revisions = docs.map((doc) => ({ id: doc.id, ...doc.data() }));

  if (revisions.length > 0) {
    return {
      revisions,
      lastDoc: docs[docs.length - 1],
      hasMore,
    };
  }

  const planDoc = await getProtocolDocRef(organizationId, planId).get();
  const legacyRevisions = planDoc.data()?.v2Meta?.revisions || [];
  const normalized = [...legacyRevisions]
    .sort((a, b) => {
      const aDate = normalizeFirestoreDate(a.createdOn) ?? new Date(0);
      const bDate = normalizeFirestoreDate(b.createdOn) ?? new Date(0);
      return bDate - aDate;
    })
    .slice(0, limit)
    .map((revision) => ({
      ...revision,
      kind: revision.kind || "event",
      isLegacy: true,
    }));

  return {
    revisions: normalized,
    lastDoc: null,
    hasMore: legacyRevisions.length > limit,
  };
}

export async function restoreRevision(planId, revisionId) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId || !revisionId) {
    throw new Error("Missing required restore revision parameters.");
  }

  const revisionDocRef = getRevisionCollectionRef(organizationId, planId).doc(revisionId);
  const snapshotRoot = revisionDocRef.collection("snapshot").doc("state");
  const [planSnapshotDoc, splitSnapshot, exerciseSnapshot] = await Promise.all([
    snapshotRoot.collection("plan").doc("data").get(),
    snapshotRoot.collection("splits").get(),
    snapshotRoot.collection("exercises").get(),
  ]);

  if (!planSnapshotDoc.exists) {
    throw new Error("No snapshot found for this revision. Restore is unavailable.");
  }

  const restoredPlan = { ...planSnapshotDoc.data() };
  if ("__meta" in restoredPlan) {
    delete restoredPlan.__meta;
  }
  restoredPlan.id = planId;
  restoredPlan.updatedOn = new Date();
  restoredPlan.v2Meta = {
    ...(restoredPlan.v2Meta || {}),
    lastEditedByEditor: "v2",
  };

  const liveSplits = await firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("protocolSplits")
    .where("protocolId", "==", planId)
    .get();

  const liveExercises = await firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("protocolExercises")
    .where("protocolId", "==", planId)
    .get();

  const deletes = [];
  liveExercises.docs.forEach((doc) => deletes.push((batch) => batch.delete(doc.ref)));
  liveSplits.docs.forEach((doc) => deletes.push((batch) => batch.delete(doc.ref)));
  await commitWritesInChunks(deletes);

  const writes = [];
  writes.push((batch) => {
    batch.set(getProtocolDocRef(organizationId, planId), restoredPlan, { merge: true });
  });

  splitSnapshot.docs.forEach((doc) => {
    const split = doc.data();
    writes.push((batch) => {
      const splitRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocolSplits")
        .doc(split.id);
      batch.set(splitRef, split, { merge: true });
    });
  });

  exerciseSnapshot.docs.forEach((doc) => {
    const exercise = doc.data();
    writes.push((batch) => {
      const exerciseRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("protocolExercises")
        .doc(exercise.id);
      batch.set(exerciseRef, exercise, { merge: true });
    });
  });

  await commitWritesInChunks(writes);
  await appendRevision(planId, {
    action: "restore",
    summary: `Restored from revision ${revisionId}`,
    sourceRevisionId: revisionId,
  });
}

async function getPlanSnapshotForDiff(planId) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId) {
    return null;
  }

  const [planDoc, splitSnapshot, exerciseSnapshot] = await Promise.all([
    getProtocolDocRef(organizationId, planId).get(),
    firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolSplits")
      .where("protocolId", "==", planId)
      .get(),
    firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("protocolExercises")
      .where("protocolId", "==", planId)
      .get(),
  ]);

  if (!planDoc.exists) {
    return null;
  }

  return {
    plan: mapPlanData(planDoc),
    splits: splitSnapshot.docs.map((doc) => doc.data()),
    exercises: exerciseSnapshot.docs.map((doc) => doc.data()),
  };
}

export async function comparePlanVersions({
  fromPlanId,
  toPlanId,
} = {}) {
  if (!fromPlanId || !toPlanId) {
    throw new Error("Both versions are required for comparison.");
  }

  const [fromSnapshot, toSnapshot] = await Promise.all([
    getPlanSnapshotForDiff(fromPlanId),
    getPlanSnapshotForDiff(toPlanId),
  ]);

  if (!fromSnapshot || !toSnapshot) {
    throw new Error("One or both selected versions are unavailable.");
  }

  return buildVersionDiff({
    fromSnapshot,
    toSnapshot,
  });
}

export async function getMetrics(lastDays = 14) {
  const organizationId = getOrganizationId();
  if (!organizationId) {
    return [];
  }

  const snapshot = await firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("trainingPlansV2MetricsDaily")
    .orderBy("date", "desc")
    .limit(lastDays)
    .get();

  return snapshot.docs.map((d) => d.data());
}

const EDITOR_STAGING_DOC_ID = "current";

function getEditorStagingDocRef(organizationId, planId) {
  return getProtocolDocRef(organizationId, planId).collection("editorStaging").doc(EDITOR_STAGING_DOC_ID);
}

/**
 * Staged editor payload for active+assigned plans: not read by clients until publish.
 * Shape: { plan, splits, exercisesBySplitId: { [splitId]: exercise[] } }
 */
export async function getEditorStaging(planId) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId) {
    return null;
  }
  const ref = getEditorStagingDocRef(organizationId, planId);
  const snap = await ref.get();
  if (!snap.exists) {
    return null;
  }
  return snap.data();
}

/**
 * Read whether each plan has an editor staging doc (`protocols/{id}/editorStaging/current`).
 * Uses parallel `ref.get()` — compat `firebase.firestore()` has no `getAll` (modular-only API).
 * Returns `{ [planId]: boolean }` for plans that were queried.
 */
export async function getEditorStagingPresenceByPlanIds(planIds = []) {
  const organizationId = getOrganizationId();
  if (!organizationId) {
    return {};
  }
  const unique = [...new Set((planIds || []).filter(Boolean))];
  if (!unique.length) {
    return {};
  }
  const refs = unique.map((id) => getEditorStagingDocRef(organizationId, id));
  const snaps = await Promise.all(refs.map((ref) => ref.get()));
  const result = {};
  snaps.forEach((snap, idx) => {
    result[unique[idx]] = snap.exists;
  });
  return result;
}

function stripUndefined(value) {
  if (Array.isArray(value)) {
    return value.map(stripUndefined);
  }
  if (value !== null && typeof value === "object" && !(value instanceof Date)) {
    const result = {};
    for (const [k, v] of Object.entries(value)) {
      if (v !== undefined) {
        result[k] = stripUndefined(v);
      }
    }
    return result;
  }
  return value;
}

export async function saveEditorStaging(planId, snapshot) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId) {
    throw new Error("Organization and plan id are required.");
  }
  const ref = getEditorStagingDocRef(organizationId, planId);
  await ref.set(
    stripUndefined({
      ...snapshot,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    }),
    { merge: false },
  );
}

export async function deleteEditorStaging(planId) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId) {
    return;
  }
  await getEditorStagingDocRef(organizationId, planId).delete();
}

export async function bumpProtocolChangedForPlan(planId) {
  const organizationId = getOrganizationId();
  if (!organizationId || !planId) {
    return;
  }
  const snapshot = await firebase
    .firestore()
    .collection("organizations")
    .doc(organizationId)
    .collection("clients")
    .get();
  const now = new Date();
  await Promise.all(
    snapshot.docs.map((doc) => {
      const data = doc.data();
      const uid = doc.id;
      const isPrimary = data.protocolId === planId;
      const alt = Array.isArray(data.alternativeProtocols) ? data.alternativeProtocols : [];
      const isAlt = alt.includes(planId);
      if (!isPrimary && !isAlt) {
        return Promise.resolve();
      }
      return ClientData.updateClient(uid, { protocolChanged: now });
    }),
  );
}

/**
 * Applies a full staging snapshot to live protocol + splits + exercises, then clears staging.
 */
export async function publishStagingSnapshotToLive(planId, snapshot) {
  if (!planId || !snapshot?.plan) {
    throw new Error("Plan id and snapshot are required.");
  }
  const organizationId = getOrganizationId();
  if (!organizationId) {
    throw new Error("Organization is required.");
  }

  const liveSplits = await getSplits(planId);
  const initialLiveIds = new Set(liveSplits.map((s) => s.id));
  const stagingSplits = Array.isArray(snapshot.splits) ? snapshot.splits : [];
  const stagingIds = new Set(stagingSplits.map((s) => s.id));
  const exercisesBySplitId = snapshot.exercisesBySplitId && typeof snapshot.exercisesBySplitId === "object"
    ? snapshot.exercisesBySplitId
    : {};

  const currentPlan = await getPlanById(planId);
  const mergedPlan = {
    ...snapshot.plan,
    id: planId,
    planType: snapshot.plan?.planType ?? currentPlan?.planType ?? "template",
    clientUID:
      (snapshot.plan?.planType ?? currentPlan?.planType) === "client"
        ? snapshot.plan?.clientUID ?? currentPlan?.clientUID
        : null,
    v2Meta: {
      ...(currentPlan?.v2Meta || {}),
      ...(snapshot.plan?.v2Meta || {}),
      status: "active",
    },
    splitCount: stagingSplits.length,
  };

  await savePlanMetadata(planId, mergedPlan);

  for (const live of liveSplits) {
    if (!stagingIds.has(live.id)) {
      await deleteSplit(planId, live.id);
    }
  }

  for (const split of stagingSplits) {
    const isAdd = !initialLiveIds.has(split.id);
    await upsertSplit(
      {
        ...split,
        protocolId: planId,
      },
      isAdd,
    );
  }

  await reorderSplits(planId, stagingSplits);

  for (const split of stagingSplits) {
    if (split.isRestSplit) {
      continue;
    }
    const splitId = split.id;
    const liveEx = await getSplitExercises(splitId);
    if (liveEx.length) {
      await bulkDeleteExercises(liveEx);
    }
    const stagingEx = exercisesBySplitId[splitId] || [];
    const withIds = stagingEx.map((exercise, index) => ({
      ...exercise,
      protocolId: planId,
      splitId,
      index: exercise.index ?? index,
    }));
    if (withIds.length) {
      await bulkUpsertExercises(withIds);
    }
  }

  await deleteEditorStaging(planId);
  await appendRevision(planId, {
    action: "publish_staging",
    summary: "Published staged editor changes to live plan",
  });
  await bumpProtocolChangedForPlan(planId);
}
